import ClaudeLogoIcon from "@/svg/brand/claude-logo-icon.svg?react";
import CohesiveLogoIcon from "@/svg/brand/cohesive-logo-icon.svg?react";
import OpenAILogoIcon from "@/svg/brand/openai-logo-icon.svg?react";
import {
  HARNESS_IDS,
  HarnessCapability,
  HarnessId,
  MODEL_SWITCH_SCOPES,
} from "@cohesive/api-client";
import { SquareTerminal } from "lucide-react";
import type { ComponentType, SVGProps } from "react";

interface HarnessMeta {
  /** Omitted where the catalog's own label is the one to show. */
  label?: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
}

/**
 * Per-harness display metadata (label + icon), keyed by harness id — the sibling of MODE_META,
 * which is keyed by session mode and answers a different question. Drives the chat composer's
 * harness selector and the read-only indicator inside a stamped chat.
 *
 * A label here overrides the catalog's; the icon is the only thing every entry must carry,
 * since the wire has none. MODE_META still brands the legacy loop "Cohesive Assistant" — this
 * is the selector, where it sits beside three other engines and the server's "Cohesive" reads
 * better.
 */
export const HARNESS_META: Record<HarnessId, HarnessMeta> = {
  [HARNESS_IDS.COHESIVE]: { Icon: CohesiveLogoIcon },
  // Labelled here too: a stamped chat still states it once the flag, and the catalog row, is gone.
  [HARNESS_IDS.COHESIVE_CLAUDE_CODE]: {
    label: "Cohesive",
    Icon: CohesiveLogoIcon,
  },
  [HARNESS_IDS.CLAUDE_CODE]: { label: "Claude", Icon: ClaudeLogoIcon },
  [HARNESS_IDS.OPENCODE]: { label: "OpenCode", Icon: SquareTerminal },
  [HARNESS_IDS.CODEX]: { label: "ChatGPT", Icon: OpenAILogoIcon },
};

/**
 * Display label for a harness. The catalog is the server's, so an id this build has no entry
 * for falls back to the label the server sent, then to the id itself.
 */
export function harnessLabel(
  harness: string | undefined,
  serverLabel?: string,
): string {
  if (!harness) return serverLabel ?? "";
  return HARNESS_META[harness as HarnessId]?.label ?? serverLabel ?? harness;
}

/** Icon for a harness; undefined for an id this build doesn't know. */
export function harnessIcon(
  harness: string | undefined,
): HarnessMeta["Icon"] | undefined {
  return harness ? HARNESS_META[harness as HarnessId]?.Icon : undefined;
}

/**
 * The catalog row a harness is offered as: where the server lists Cohesive as cohesive-claude-code
 * in place of the legacy loop, it starts a cohesive choice there too.
 */
export function offeredHarness(
  harness: HarnessId,
  capabilities: HarnessCapability[] | undefined,
): HarnessId {
  return harness === HARNESS_IDS.COHESIVE &&
    !!capabilities?.some((c) => c.id === HARNESS_IDS.COHESIVE_CLAUDE_CODE)
    ? HARNESS_IDS.COHESIVE_CLAUDE_CODE
    : harness;
}

/**
 * Whether a session runs on an agent harness rather than the legacy loop. Sessions that
 * predate the stamp carry no harness at all, which is cohesive.
 */
export function runsOnHarness(harness: string | undefined): boolean {
  return !!harness && harness !== HARNESS_IDS.COHESIVE;
}

/**
 * Whether the harness seat belongs on a surface at all: a thing already running on one states
 * it however the flag has since moved, and otherwise the seat waits for both the flag and the
 * catalog, since with the runtime off cohesive is the only choice and a one-option picker is
 * noise. Shared because the composer and the automation form must not drift on it.
 */
export function harnessSeatVisible(
  harness: string | undefined,
  harnessRuntime: boolean,
  capabilities: HarnessCapability[] | undefined,
): boolean {
  return runsOnHarness(harness) || (harnessRuntime && !!capabilities);
}

/** The model-bearing part of a harness capability or a subscription agent. */
export interface ModelCatalog {
  models?: HarnessCapability["models"];
  default_model_id?: string;
  resolved_default_model_id?: string;
  resolved_default_effort_id?: string;
}

/**
 * The model a harness runs when the user has not picked one: the server's default, else the
 * first model it offers — a harness with models must always show one, since an empty model is
 * only resolvable server-side and there is nothing to render. `followPreference` puts the
 * user's preferred model first, for a seat where the server fills an omitted model with it.
 */
export function defaultModelFor(
  capability: ModelCatalog | undefined,
  followPreference = false,
): string | undefined {
  if (!capability?.models?.length) return undefined;
  const candidates = [
    followPreference ? capability.resolved_default_model_id : undefined,
    capability.default_model_id,
  ];
  return (
    candidates.find((id) => capability.models?.some((m) => m.id === id)) ??
    capability.models[0]?.id
  );
}

/**
 * The effort a model runs when none is picked: the user's preferred effort where their
 * preference chose this model, else the model's own default — what the server resolves.
 */
export function defaultEffortFor(
  capability: ModelCatalog | undefined,
  modelId: string | undefined,
): string | undefined {
  if (modelId && modelId === capability?.resolved_default_model_id) {
    return capability.resolved_default_effort_id;
  }
  return capability?.models?.find((m) => m.id === modelId)?.default_effort_id;
}

/**
 * Whether a live session running `from` may move to `to`. Mirrors the server's check so the
 * menu can dim what it would refuse; the server still enforces it, and a scope this build
 * doesn't know reads as unrestricted rather than locking the user out of their own catalog.
 *
 * A session with no model resolved yet has no provider to be held to.
 */
export function modelSwitchAllowed(
  capability: HarnessCapability | undefined,
  from: string | undefined,
  to: string,
): boolean {
  if (capability?.model_switch_scope !== MODEL_SWITCH_SCOPES.SAME_PROVIDER) {
    return true;
  }
  return !from || modelProvider(from) === modelProvider(to);
}

/** Mirrors agentd's opencode split: "provider/model", and a bare id is anthropic. */
function modelProvider(model: string): string {
  const separator = model.indexOf("/");
  return separator === -1 ? "anthropic" : model.slice(0, separator);
}
